// 用户详情对话框(admin 专用) — 对齐 docs/ui_dialog 规范
// 列表页点击用户行打开本对话框;标题栏右上角 ✕ 关闭。
// 内部管理操作(切换管理员/重置密码/删除)保留;重置密码/删除为二级弹窗。
import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Button,
  Text,
  Avatar,
  Badge,
  Switch,
  Input,
  Label,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
} from '@fluentui/react-components';
import { KeyRegular, DeleteRegular } from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { explainError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { DialogCloseTitle } from '@/components/DialogCloseTitle';
import { DangerButton } from '@/components/DangerButton';
import { HelpPopover } from '@/components/HelpPopover';
import { toast } from '@/lib/toast';

const STATUS_META: Record<string, { color: 'success' | 'subtle' | 'warning'; label: string }> = {
  active: { color: 'success', label: '正常' },
  suspended: { color: 'subtle', label: '已停用' },
  pending: { color: 'warning', label: '待激活' },
};

const useStyles = makeStyles({
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 0',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
  headerTitle: {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase400,
  },
  headerSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  card: { width: '100%' },
  cardHeader: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  // ── 描述网格 2 列 ──
  descGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  descCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  descLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  descValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // ── 管理操作行 ──
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '8px 0',
  },
  actionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  actionDangerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorStatusDangerForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  actionDesc: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export function UserDetailDialog({
  open,
  username,
  onClose,
}: {
  open: boolean;
  username: string | null;
  onClose: () => void;
}) {
  const styles = useStyles();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetPwd, setResetPwd] = useState('');
  const [resetting, setResetting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 从列表数据中查找该用户(列表已加载 100 条)
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers(100, 0),
    enabled: !!me?.is_admin,
  });

  const user = username ? data?.users.find((u) => u.username === username) : undefined;

  // 关闭父对话框时,连带重置二级弹窗与密码输入,避免下次打开残留
  function handleClose() {
    setResetOpen(false);
    setDeleteOpen(false);
    setResetPwd('');
    onClose();
  }

  const isSelf = user?.username === me?.username;
  const statusMeta = STATUS_META[user?.status ?? ''] ?? STATUS_META.active;

  async function onToggleAdmin(next: boolean) {
    if (!user) return;
    try {
      await adminApi.patchUser(user.username, { is_admin: next });
      toast.success(
        next ? `已将 ${user.username} 设为管理员` : `已取消 ${user.username} 的管理员权限`,
      );
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (e) {
      toast.error(explainError(e));
    }
  }

  async function onDelete() {
    if (!user) return;
    setDeleteOpen(false);
    setDeleting(true);
    try {
      await adminApi.deleteUser(user.username);
      toast.success(`已删除用户 ${user.username}`);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      handleClose();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setDeleting(false);
    }
  }

  async function onResetPassword() {
    if (!user) return;
    if (resetPwd.length < 8) {
      toast.warning('密码至少 8 位');
      return;
    }
    setResetting(true);
    try {
      await adminApi.resetPassword(user.username, resetPwd);
      toast.success(`已重置 ${user.username} 的密码`);
      setResetOpen(false);
      setResetPwd('');
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setResetting(false);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(_, d) => !d.open && handleClose()}>
      <DialogSurface style={{ width: 560, maxWidth: '92vw' }}>
        <DialogBody>
          <DialogCloseTitle
            title={user ? `用户详情 · ${user.username}` : '用户详情'}
            onClose={handleClose}
          />
          <DialogContent style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {isLoading ? (
              <div className={styles.loadingWrap}>
                <Spinner size="large" label="加载中" />
              </div>
            ) : !user ? (
              <Text>未找到用户「{username ?? ''}」</Text>
            ) : (
              <>
                {/* 头部 */}
                <div className={styles.headerRow}>
                  <Avatar
                    size={56}
                    name={user.username}
                    image={user.avatar_url ? { src: user.avatar_url } : undefined}
                    color="brand"
                  />
                  <div>
                    <div className={styles.headerTitle}>
                      <span>{user.username}</span>
                      {user.is_admin && (
                        <Badge appearance="filled" color="warning">
                          管理员
                        </Badge>
                      )}
                      <Badge appearance="tint" color={statusMeta.color}>
                        {statusMeta.label}
                      </Badge>
                    </div>
                    <Text className={styles.headerSub}>{user.email}</Text>
                  </div>
                </div>

                {/* 用户信息 */}
                <Card appearance="outline" className={styles.card}>
                  <CardHeader header={<Text className={styles.cardHeader}>用户信息</Text>} />
                  <div className={styles.descGrid}>
                    <div className={styles.descCell}>
                      <Text className={styles.descLabel}>用户名</Text>
                      <Text className={styles.descValue}>{user.username}</Text>
                    </div>
                    <div className={styles.descCell}>
                      <Text className={styles.descLabel}>邮箱</Text>
                      <Text className={styles.descValue}>{user.email}</Text>
                    </div>
                    <div className={styles.descCell}>
                      <Text className={styles.descLabel}>昵称</Text>
                      <Text className={styles.descValue}>{user.display_name ?? '—'}</Text>
                    </div>
                    <div className={styles.descCell}>
                      <Text className={styles.descLabel}>状态</Text>
                      <Text className={styles.descValue}>
                        <Badge appearance="tint" color={statusMeta.color}>
                          {statusMeta.label}
                        </Badge>
                      </Text>
                    </div>
                    <div className={styles.descCell}>
                      <Text className={styles.descLabel}>管理员权限</Text>
                      <Text className={styles.descValue}>{user.is_admin ? '是' : '否'}</Text>
                    </div>
                    <div className={styles.descCell}>
                      <Text className={styles.descLabel}>创建时间</Text>
                      <Text className={styles.descValue}>
                        {new Date(user.created_at).toLocaleString('zh-CN')}
                      </Text>
                    </div>
                  </div>
                </Card>

                {/* 管理操作 */}
                <Card appearance="outline" className={styles.card}>
                  <CardHeader header={<Text className={styles.cardHeader}>管理操作</Text>} />

                  <div className={styles.actionRow}>
                    <div>
                      <Text className={styles.actionTitle}>管理员权限</Text>
                      <br />
                      <Text className={styles.actionDesc}>
                        授予该用户系统管理员权限,可管理所有用户和仓库。
                      </Text>
                    </div>
                    <Switch
                      checked={user.is_admin}
                      disabled={isSelf}
                      onChange={(_, d) => onToggleAdmin(d.checked)}
                    />
                  </div>

                  <div className={styles.actionRow}>
                    <div>
                      <Text className={styles.actionTitle}>重置密码</Text>
                      <br />
                      <Text className={styles.actionDesc}>为该用户设置新的登录密码。</Text>
                    </div>
                    <Button
                      appearance="outline"
                      icon={<KeyRegular />}
                      onClick={() => setResetOpen(true)}
                    >
                      重置密码
                    </Button>
                  </div>

                  <div className={styles.actionRow}>
                    <div>
                      <Text className={styles.actionDangerTitle}>删除用户</Text>
                      <br />
                      <Text className={styles.actionDesc}>
                        删除后无法恢复,其仓库将一并移除。
                      </Text>
                    </div>
                    <DangerButton
                      icon={<DeleteRegular />}
                      disabled={isSelf || deleting}
                      onClick={() => setDeleteOpen(true)}
                    >
                      删除用户
                    </DangerButton>
                  </div>
                </Card>
              </>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/* 重置密码二级弹窗 */}
      <Dialog open={resetOpen} onOpenChange={(_, d) => setResetOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>重置密码 — {user?.username ?? ''}</DialogTitle>
            <DialogContent>
              <Label required style={{ display: 'block', marginBottom: '4px' }}>
                新密码 <HelpPopover content="为该用户设置的新登录密码,至少 8 位。设置后该用户需用新密码登录。" />
              </Label>
              <Input
                type="password"
                placeholder="输入新密码(至少 8 位)"
                value={resetPwd}
                onChange={(_, d) => setResetPwd(d.value)}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setResetOpen(false);
                    setResetPwd('');
                  }}
                >
                  取消
                </Button>
              </DialogTrigger>
              <Button appearance="primary" disabled={resetting} onClick={onResetPassword}>
                {resetting ? '重置中…' : '重置'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除确认二级弹窗 */}
      <Dialog
        modalType="alert"
        open={deleteOpen}
        onOpenChange={(_, d) => setDeleteOpen(d.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除该用户?</DialogTitle>
            <DialogContent>删除后无法恢复,其仓库将一并移除</DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">取消</Button>
              </DialogTrigger>
              <DangerButton onClick={onDelete}>删除</DangerButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}